/** The "80 %" generator: a regular run of bays, ready to be edited freely. */

import { CFG, MM } from "../data/haki.ts";
import { addBeam, addLeg, autoRailsFromDecks, clearModel } from "./operations.ts";
import { composeSpires, type PlankFamily } from "./solver.ts";
import type { DeckCorners, Point, ScaffoldModel } from "./types.ts";

/** Node levels per 2.0 m lift. */
const LEVELS_PER_LIFT = 4;

/** Vertical diagonals go in the end bays and at least every fifth bay (RISE C900677). */
const DIAGONAL_EVERY = 5;

export type DeckMode = "top" | "all" | "none";

export type GeneratorSettings = Readonly<{
  numBays: number;
  numLifts: number;
  bayLen: number;
  bayWidth: number;
  deckMode: DeckMode;
  plankFam: PlankFamily;
  /** Bay index that gets the hatch deck, or -1 for none. */
  hatchBay: number;
  guard: boolean;
  diag: boolean;
}>;

function deckedLifts(mode: DeckMode, numLifts: number): number[] {
  if (mode === "top") {
    return numLifts > 0 ? [numLifts] : [];
  }
  if (mode === "all") {
    return Array.from({ length: numLifts }, (_, index) => index + 1);
  }

  return [];
}

type Grid = (i: number, j: number) => Point;

function buildLegs(model: ScaffoldModel, numBays: number, segments: number[], at: Grid): void {
  for (let i = 0; i <= numBays; i++) {
    for (const j of [0, 1]) {
      const q = at(i, j);
      addLeg(model, q.x, q.z, [...segments]);
    }
  }
}

function buildBeams(model: ScaffoldModel, numBays: number, numLifts: number, at: Grid): void {
  for (let lift = 1; lift <= numLifts; lift++) {
    const p = lift * LEVELS_PER_LIFT;
    for (let i = 0; i < numBays; i++) {
      addBeam(model, at(i, 0), at(i + 1, 0), p, "lb");
      addBeam(model, at(i, 1), at(i + 1, 1), p, "lb");
    }
    for (let i = 0; i <= numBays; i++) {
      addBeam(model, at(i, 0), at(i, 1), p, "erb");
    }
  }
}

function buildDecks(model: ScaffoldModel, settings: GeneratorSettings, at: Grid): void {
  const { numBays, numLifts, bayLen, bayWidth } = settings;
  const canHatch = CFG.hatch.art[bayLen] !== undefined;

  for (const lift of deckedLifts(settings.deckMode, numLifts)) {
    for (let i = 0; i < numBays; i++) {
      const corners: DeckCorners = [at(i, 0), at(i + 1, 0), at(i, 1), at(i + 1, 1)];
      model.decks.push({
        c: corners,
        p: lift * LEVELS_PER_LIFT,
        hatch: i === settings.hatchBay && canHatch,
        removed: new Set(),
        len: bayLen,
        wid: bayWidth,
        fam: settings.plankFam,
      });
    }
  }
}

/** End bays plus every fifth, alternating direction per lift. */
function buildDiagonals(model: ScaffoldModel, numBays: number, numLifts: number, at: Grid): void {
  const braced = new Set([0, numBays - 1]);
  for (let i = 0; i < numBays; i += DIAGONAL_EVERY) {
    braced.add(i);
  }

  for (const i of braced) {
    for (let lift = 0; lift < numLifts; lift++) {
      const flip = lift % 2 === 0;
      model.diags.push({
        a: at(flip ? i : i + 1, 1),
        pa: lift * LEVELS_PER_LIFT,
        b: at(flip ? i + 1 : i, 1),
        pb: lift * LEVELS_PER_LIFT + LEVELS_PER_LIFT,
      });
    }
  }
}

/**
 * Replace the model with a regular scaffold run: standards on a grid, ledgers
 * and transoms at every lift, decking per `deckMode`, and — when asked —
 * guardrails on open edges and diagonals in the end bays plus every fifth.
 */
export function generate(model: ScaffoldModel, settings: GeneratorSettings): void {
  clearModel(model);

  const { numBays, numLifts, bayLen, bayWidth } = settings;
  const L = bayLen * MM;
  const W = bayWidth * MM;
  const at: Grid = (i, j) => ({ x: +(i * L).toFixed(3), z: +(j * W).toFixed(3) });

  buildLegs(model, numBays, composeSpires(numLifts * 2000 + 1000), at);
  buildBeams(model, numBays, numLifts, at);
  buildDecks(model, settings, at);

  if (settings.guard) {
    autoRailsFromDecks(model);
  }
  if (settings.diag) {
    buildDiagonals(model, numBays, numLifts, at);
  }
}
